import { defineComponent, PropType, reactive, watch } from '@vue/composition-api';
import { ConnectionStatus } from "@/views/connections/connections-types";
import { JobylonService, addJobylon, deleteJobylon } from '@/functions/services-functions';
import { inputDialogue, confirmDialogue, generalError } from '@/functions/system-modal-functions';
import { i18n } from "@/utilities/vue-i18n";
import { ModalIconType } from '../system-modals/system-modals';

type SettingsType = {
  connection: JobylonService;
  loading: boolean;
  company: number;
};

const validateNumber = (input: string) => {
  try {
    const number = parseInt(input, 10);

    return !isNaN(number) && number > 0;
  }
  catch {
    return false;
  }
};

export default defineComponent({
  props: {
    connection: {
      required: true,
      type: Object as PropType<JobylonService>,
    },
    companyId: {
      required: false,
      type: Number,
    },
    allowDelete: {
      required: false,
      type: Boolean,
      default: false,
    },
    isAdmin: {
      required: false,
      type: Boolean,
      default: false,
    },
    loading: {
      required: false,
      type: Boolean,
      default: false,
    }
  },
  setup(props, context) {
    const settings = reactive<SettingsType>({
      connection: { ...props.connection },
      loading: props.loading,
      company: props.companyId ?? +context.root.$route.params.company
    });

    watch(() => props.connection, (value) => settings.connection = { ...value }, { deep: true });
    watch(() => props.companyId, (value) => settings.company = value ?? +context.root.$route.params.company);
    watch(() => props.loading, (value) => settings.loading = value ?? settings.loading);

    const addJobylonCompanyId = async () => {
      if (settings.loading) {
        return;
      }

      settings.loading = true;

      const dialogue = await inputDialogue({
        title: 'Jobylon',
        message: i18n.t('components.jobylon-connection.add-jobylon-company-id'),
        validation: validateNumber,
      });

      if (dialogue.confirmed) {
        const input = dialogue.input;

        const service = await addJobylon(settings.company, +input, props.isAdmin);

        if (service) {
          settings.connection = service;

          context.emit('updated', settings.connection);
        }
        else {
          await generalError(false);
        }
      }

      settings.loading = false;
    };

    const removeJobylonCompanyId = async () => {
      if (!props.allowDelete || settings.loading) {
        return;
      }

      settings.loading = true;

      const confirmed = await confirmDialogue(
        'Jobylon',
        i18n.t('components.jobylon-connection.remove-jobylon-company-id'),
        ModalIconType.warning
      );

      if (confirmed) {
        if (await deleteJobylon(settings.connection.id)) {
          context.emit('updated', null);
        }
        else {
          await generalError(false);
        }
      }

      settings.loading = false;
    };

    return {
      settings,
      ConnectionStatus,
      addJobylonCompanyId,
      removeJobylonCompanyId
    }
  },
})